import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import { ImageUploadError, uploadImage } from '@/lib/imageUpload';
import type { TeamMember, CompanyTeam, CompanyLocalization, CompanyLink, Permission } from '@/types';
import { useUpgradeWallStore } from '@/stores/upgradeWallStore';

type MemberType = 'internal' | 'external';

export interface UseCompanySettingsReturn {
  // Permissions — derived from user.memberships[selectedCompany.id]
  /** User's permission level in the selected company ('admin' | 'write' | 'read'). */
  myPermission: Permission;
  /** True if user can invite/edit/remove members (admin only). */
  canManageMembers: boolean;
  /** True if user can edit company settings (admin or write). */
  canEditCompany: boolean;

  // General — company identity
  companyName: string;
  setCompanyName: (v: string) => void;
  companyPublicName: string;
  setCompanyPublicName: (v: string) => void;
  companyDescription: string;
  setCompanyDescription: (v: string) => void;
  companyEmployeeCount: string;
  setCompanyEmployeeCount: (v: string) => void;
  avgSalaryMin: string;
  setAvgSalaryMin: (v: string) => void;
  avgSalaryMax: string;
  setAvgSalaryMax: (v: string) => void;
  avgSalaryCurrency: string;
  setAvgSalaryCurrency: (v: string) => void;
  /** Tenant default AI/email output language — inherited by new job positions. */
  defaultOutputLanguage: 'it' | 'en';
  setDefaultOutputLanguage: (v: 'it' | 'en') => void;
  savingGeneral: boolean;
  // General — localization (offices) / links / logo
  offices: CompanyLocalization[];
  setOffices: React.Dispatch<React.SetStateAction<CompanyLocalization[]>>;
  links: CompanyLink[];
  setLinks: React.Dispatch<React.SetStateAction<CompanyLink[]>>;
  logoUrl: string;
  setLogoUrl: (v: string) => void;
  savedLogoUrl: string;
  setSavedLogoUrl: (v: string) => void;
  // Teams
  teams: CompanyTeam[];
  setTeams: React.Dispatch<React.SetStateAction<CompanyTeam[]>>;
  teamDialog: boolean;
  setTeamDialog: (v: boolean) => void;
  editTeam: { id?: string; name: string; description: string };
  setEditTeam: React.Dispatch<React.SetStateAction<{ id?: string; name: string; description: string }>>;
  handleSaveTeam: () => Promise<void>;
  handleDeleteTeam: (teamId: string) => Promise<void>;
  // Delete company
  deleteCompanyOpen: boolean;
  setDeleteCompanyOpen: (v: boolean) => void;
  handleDeleteCompany: () => Promise<void>;
  // Members
  members: TeamMember[];
  setMembers: React.Dispatch<React.SetStateAction<TeamMember[]>>;
  filteredMembers: TeamMember[];
  addOpen: boolean;
  setAddOpen: (v: boolean) => void;
  /** Pre-check cap before opening invite dialog. Use this for the button click. */
  handleInviteMemberClick: () => Promise<void>;
  checkingMemberCap: boolean;
  editMember: TeamMember | null;
  setEditMember: (v: TeamMember | null) => void;
  removeId: string | null;
  setRemoveId: (v: string | null) => void;
  addEmail: string;
  setAddEmail: (v: string) => void;
  // Sprint 6 (S6.A): optional pre-fill for the invitation email + member doc.
  addFirstName: string;
  setAddFirstName: (v: string) => void;
  addLastName: string;
  setAddLastName: (v: string) => void;
  addPermission: Permission;
  setAddPermission: (v: Permission) => void;
  addRole: Permission;
  setAddRole: (v: Permission) => void;
  addType: MemberType;
  setAddType: (v: MemberType) => void;
  addTeam: string;
  setAddTeam: (v: string) => void;
  editPermission: Permission;
  setEditPermission: (v: Permission) => void;
  editRole: Permission;
  setEditRole: (v: Permission) => void;
  editMemberTeam: string;
  setEditMemberTeam: (v: string) => void;
  editType: MemberType;
  setEditType: (v: MemberType) => void;
  editActive: boolean;
  setEditActive: (v: boolean) => void;
  activeTab: string;
  setActiveTab: (v: string) => void;
  teamFilterName: string | null;
  handleTeamClick: (teamId: string, teamName: string) => Promise<void>;
  clearTeamFilter: () => void;
  openEdit: (m: TeamMember) => void;
  copyInviteLink: (memberId: string) => void;
  // Sprint 6 (S6.B): re-send a stale/expired invitation. The BE regenerates
  // token + expiresAt and re-runs the pipeline so the invitee gets a fresh email.
  handleResendInvitation: (invitationId: string) => Promise<void>;
  handleAddMember: () => Promise<void>;
  isSendingInvite: boolean;
  handleEditSave: () => Promise<void>;
  handleRemove: () => Promise<void>;
  uploadingLogo: boolean;
  /**
   * Handles the picked file from the logo `<input type="file">`. Wired
   * to a label-driven file picker on SettingsPage (the previous
   * `document.createElement('input').click()` pattern was unreliable
   * in embedded preview environments).
   */
  handleLogoFileSelected: React.ChangeEventHandler<HTMLInputElement>;
  handleRemoveLogo: () => Promise<void>;
  handleSaveGeneral: () => Promise<void>;
  OFFICE_TYPES: string[];
  LINK_TYPES: string[];
}

export function useCompanySettings(): UseCompanySettingsReturn {
  const { selectedCompany, setSelectedCompany, user } = useAuth();
  const { t } = useLanguage();

  // Derive permission from user.memberships for the selected company
  const myPermission: Permission = selectedCompany && user?.memberships?.[selectedCompany.id]?.permission || 'read';
  const canManageMembers = myPermission === 'admin';
  const canEditCompany = myPermission === 'admin' || myPermission === 'write';

  const OFFICE_TYPES = ['Headquarters', 'Branch', 'Remote Hub', 'Satellite'];
  const LINK_TYPES = ['Website', 'LinkedIn', 'Twitter / X', 'GitHub', 'Glassdoor', 'Instagram', 'Facebook'];

  const [companyName, setCompanyName] = useState(selectedCompany?.name || '');
  const [companyPublicName, setCompanyPublicName] = useState(selectedCompany?.publicName || '');
  const [companyDescription, setCompanyDescription] = useState(selectedCompany?.description || '');
  const [companyEmployeeCount, setCompanyEmployeeCount] = useState(selectedCompany?.employeeCount ? String(selectedCompany.employeeCount) : '');
  const [avgSalaryMin, setAvgSalaryMin] = useState('');
  const [avgSalaryMax, setAvgSalaryMax] = useState('');
  const [avgSalaryCurrency, setAvgSalaryCurrency] = useState('EUR');
  const [savingGeneral, setSavingGeneral] = useState(false);

  const [offices, setOffices] = useState<CompanyLocalization[]>([]);
  const [links, setLinks] = useState<CompanyLink[]>([]);
  const [logoUrl, setLogoUrl] = useState(selectedCompany?.logoUrl || '');
  const [savedLogoUrl, setSavedLogoUrl] = useState(selectedCompany?.logoUrl || '');
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Sprint 1 (D-R3.3 / BUG-10): tenant-wide AI/email language default. Falls
  // back to 'it' (BE default) when the field is missing on legacy companies.
  const [defaultOutputLanguage, setDefaultOutputLanguage] = useState<'it' | 'en'>(
    selectedCompany?.defaultOutputLanguage ?? 'it'
  );

  const [teams, setTeams] = useState<CompanyTeam[]>([]);
  const [teamDialog, setTeamDialog] = useState(false);
  const [editTeam, setEditTeam] = useState<{ id?: string; name: string; description: string }>({ name: '', description: '' });

  const [deleteCompanyOpen, setDeleteCompanyOpen] = useState(false);

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [checkingMemberCap, setCheckingMemberCap] = useState(false);
  const showUpgradeWall = useUpgradeWallStore((s) => s.show);

  /**
   * Pre-check member cap before opening the invite dialog.
   * If at cap, show UpgradeWallModal immediately instead of opening the form.
   * Called by the "Invite Member" button click handler.
   */
  const handleInviteMemberClick = useCallback(async () => {
    if (!selectedCompany) {
      setAddOpen(true);
      return;
    }
    setCheckingMemberCap(true);
    try {
      const status = await api.billing.getStatus(selectedCompany.id);
      const seats = status?.structural?.seats;
      if (seats && seats.current >= seats.limit) {
        showUpgradeWall({
          reason: 'structural_limit',
          pool_type: 'structural',
          limit_type: 'seats',
          current: seats.current,
          limit: seats.limit,
        });
        return;
      }
    } catch {
      // On error, allow opening — the BE will block if needed
    } finally {
      setCheckingMemberCap(false);
    }
    setAddOpen(true);
  }, [selectedCompany, showUpgradeWall]);
  const [editMember, setEditMember] = useState<TeamMember | null>(null);
  const [removeId, setRemoveId] = useState<string | null>(null);
  const [addEmail, setAddEmail] = useState('');
  // Sprint 6 (S6.A) — pre-fill name fields on the invite dialog.
  const [addFirstName, setAddFirstName] = useState('');
  const [addLastName, setAddLastName] = useState('');
  const [addPermission, setAddPermission] = useState<Permission>('write');
  const [addType, setAddType] = useState<MemberType>('internal');
  const [addTeam, setAddTeam] = useState('');
  const [editPermission, setEditPermission] = useState<Permission>('write');
  const [editMemberTeam, setEditMemberTeam] = useState('');
  const [editType, setEditType] = useState<MemberType>('internal');
  const [editActive, setEditActive] = useState(true);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>(() => {
    const t = searchParams.get('tab');
    return t && ['general', 'teams', 'members', 'plan', 'subscription'].includes(t) ? t : 'general';
  });
  const [teamFilterId, setTeamFilterId] = useState<string | null>(null);
  const [teamFilterName, setTeamFilterName] = useState<string | null>(null);
  const [teamFilteredMembers, setTeamFilteredMembers] = useState<TeamMember[] | null>(null);

  // Load full company data + teams + members whenever the selected company changes.
  // selectedCompany from AuthContext may only have id+name (from UserMembership denorm),
  // so we fetch the full company document to populate all form fields.
  useEffect(() => {
    if (!selectedCompany) return;
    const cid = selectedCompany.id;

    api.companies.get(cid).then(full => {
      if (!full) return;
      setSelectedCompany({ ...selectedCompany, ...full });
      setCompanyName(full.name || '');
      setCompanyPublicName(full.publicName || '');
      setCompanyDescription(full.description || '');
      setCompanyEmployeeCount(full.employeeCount ? String(full.employeeCount) : '');
      setAvgSalaryMin(full.salary?.min ? String(full.salary.min) : '');
      setAvgSalaryMax(full.salary?.max ? String(full.salary.max) : '');
      setAvgSalaryCurrency(full.salary?.currency || 'EUR');
      setLogoUrl(full.logoUrl || '');
      setSavedLogoUrl(full.logoUrl || '');
      setOffices(full.localization || []);
      setLinks(full.links || []);
    });

    api.companyTeams.list(cid).then(setTeams);
    api.team.list(cid).then(setMembers);
  }, [selectedCompany?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredMembers = teamFilteredMembers ?? members;

  const handleTeamClick = useCallback(async (teamId: string, teamName: string) => {
    if (!selectedCompany) return;
    try {
      const result = await api.companyTeams.listMembers(selectedCompany.id, teamId);
      setTeamFilteredMembers(result);
      setTeamFilterId(teamId);
      setTeamFilterName(teamName);
      setActiveTab('members');
    } catch {
      toast.error('Failed to load team members');
    }
  }, [selectedCompany]);

  const clearTeamFilter = useCallback(() => {
    setTeamFilteredMembers(null);
    setTeamFilterId(null);
    setTeamFilterName(null);
  }, []);

  const handleSaveTeam = useCallback(async () => {
    if (!selectedCompany) return;
    setTeamDialog(false);
    try {
      if (editTeam.id) {
        const updated = await api.companyTeams.update(selectedCompany.id, editTeam.id, {
          name: editTeam.name,
          description: editTeam.description || undefined,
        });
        setTeams(prev => prev.map(t => t.id === editTeam.id ? updated : t));
      } else {
        const created = await api.companyTeams.create(selectedCompany.id, {
          name: editTeam.name,
          description: editTeam.description || undefined,
        });
        setTeams(prev => [...prev, created]);
      }
      setEditTeam({ name: '', description: '' });
      toast.success('Team saved');
    } catch (err: any) {
      setTeamDialog(true); // reopen dialog so user doesn't lose input
      const msg = err?.status === 404
        ? 'Team API not yet deployed — contact backend team'
        : (err?.message || 'Failed to save team');
      toast.error(msg);
    }
  }, [editTeam, selectedCompany]);

  const handleDeleteTeam = useCallback(async (teamId: string) => {
    if (!selectedCompany) return;
    try {
      await api.companyTeams.delete(selectedCompany.id, teamId);
      setTeams(prev => prev.filter(t => t.id !== teamId));
      toast.success('Team deleted');
    } catch (err: any) {
      const msg = err?.status === 404
        ? 'Team API not yet deployed — contact backend team'
        : (err?.message || 'Failed to delete team');
      toast.error(msg);
    }
  }, [selectedCompany]);

  const openEdit = useCallback((m: TeamMember) => {
    setEditMember(m);
    setEditPermission(m.permission);
    setEditType(m.type || 'internal');
    setEditActive(m.status !== 'inactive' && m.status !== 'pending');
    setEditMemberTeam(m.teamIds?.[0] || '');
  }, []);

  const generateInviteLink = useCallback((memberId: string) => {
    const base = window.location.origin;
    return `${base}/signup?invite=${memberId}&company=${selectedCompany?.id || ''}`;
  }, [selectedCompany]);

  // Sprint 6 (S6.B) — resend the invitation email by renewing the token.
  const handleResendInvitation = useCallback(async (invitationId: string) => {
    if (!selectedCompany) return;
    try {
      await api.invitations.renew(selectedCompany.id, invitationId);
      toast.success('Invitation re-sent — the member will receive a new email.');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to resend invitation');
    }
  }, [selectedCompany]);

  const copyInviteLink = useCallback((memberId: string) => {
    navigator.clipboard.writeText(generateInviteLink(memberId));
    toast.success('Invite link copied to clipboard');
  }, [generateInviteLink]);

  const handleAddMember = useCallback(async () => {
    if (!selectedCompany || !addEmail || isSendingInvite) return;
    setIsSendingInvite(true);
    try {
      // Use invitation system — sends email via pipeline, member is created only after acceptance.
      // Do NOT call api.team.add() which bypasses the invitation flow.
      // Plan E Phase 1 Task 1.4: forward `addTeam` so the new member
      // is auto-assigned to that team at accept-time. Empty selection
      // → empty array (member starts unassigned).
      // Sprint 6 (S6.A): forward optional firstName/lastName so the BE
      // can persist them on the invitation doc + email body.
      await api.invitations.create(selectedCompany.id, {
        email: addEmail.trim().toLowerCase(),
        permission: addPermission as 'admin' | 'write' | 'read',
        type: addType as 'internal' | 'external',
        teamIds: addTeam ? [addTeam] : [],
        ...(addFirstName.trim() ? { firstName: addFirstName.trim() } : {}),
        ...(addLastName.trim() ? { lastName: addLastName.trim() } : {}),
      });
      setAddOpen(false);
      setAddEmail(''); setAddFirstName(''); setAddLastName('');
      setAddPermission('write'); setAddType('internal'); setAddTeam('');
      // Sprint 6 (S6.A): refresh the members table immediately so the
      // pending row shows up without a manual reload. Best-effort.
      try {
        const list = await api.team.list(selectedCompany.id);
        setMembers(list as TeamMember[]);
      } catch { /* leave list as-is */ }
      toast.success('Invitation sent — the member will receive an email to accept.');
    } catch (err: any) {
      // Suppress toast for 402 structural errors — the UpgradeWallModal handles those
      const is402Structural = err?.status === 402 || err?.reason === 'structural_limit';
      if (!is402Structural) {
        // v3.4 (25/05/26) — localize known invitation errors via i18n. The BE
        // sets `code` on ApiError for these (member confinement v3.4 rollout).
        let localizedMessage: string | undefined;
        if (err?.code === 'INVITATION_TARGET_IS_AGENCY_OWNER') {
          localizedMessage = t('invitation.error.target_is_agency_owner');
        } else if (err?.code === 'INVITATION_TARGET_BELONGS_TO_OTHER_OWNER') {
          localizedMessage = t('invitation.error.target_belongs_to_other_owner');
        }
        toast.error(localizedMessage || err?.message || t('invitation.error.fallback'));
      }
    } finally {
      setIsSendingInvite(false);
    }
  }, [selectedCompany, addEmail, addFirstName, addLastName, addPermission, addType, addTeam, isSendingInvite, t]);

  const handleEditSave = useCallback(async () => {
    if (!editMember || !selectedCompany) return;
    const currentTeamId = editMember.teamIds?.[0] || '';
    const teamChanged = editMemberTeam !== currentTeamId;
    const permChanged = editPermission !== editMember.permission;
    const typeChanged = editType !== (editMember.type || 'internal');
    const activeChanged = editActive !== (editMember.status !== 'inactive' && editMember.status !== 'pending');
    try {
      // PUT only when permission/type/isActive actually changed
      if (permChanged || typeChanged || activeChanged) {
        const putBody: Record<string, unknown> = {};
        if (permChanged) putBody.permission = editPermission;
        if (typeChanged) putBody.type = editType;
        if (activeChanged) putBody.isActive = editActive;
        await api.team.update(selectedCompany.id, editMember.id, putBody);
      }
      // PATCH /teams — replace full teamIds array (v5.1)
      if (teamChanged) {
        await api.team.assignTeams(selectedCompany.id, editMember.id, editMemberTeam ? [editMemberTeam] : []);
      }
      setMembers(prev => prev.map(m => m.id === editMember.id ? { ...m, permission: editPermission, type: editType, teamIds: editMemberTeam ? [editMemberTeam] : [], status: editActive ? 'active' : 'inactive' } : m));
      setEditMember(null);
      // Refresh teams to update memberIds counts
      if (teamChanged) {
        api.companyTeams.list(selectedCompany.id).then(setTeams);
      }
      toast.success('Member updated');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update member');
    }
  }, [editMember, selectedCompany, editPermission, editType, editActive, editMemberTeam]);

  const handleRemove = useCallback(async () => {
    if (!removeId || !selectedCompany) return;
    try {
      await api.team.remove(selectedCompany.id, removeId);
      setMembers(prev => prev.filter(m => m.id !== removeId));
      setRemoveId(null);
      toast.success('Member removed');
    } catch {
      toast.error('Failed to remove member');
    }
  }, [removeId, selectedCompany]);

  /**
   * handleLogoFileSelected — Firebase Storage flow (Plan B Phase 1).
   *
   * Previously this lived inside `handleUploadLogo` which built an
   * `<input>` programmatically with `document.createElement` and called
   * `.click()` on it. That pattern fails silently in the Lovable
   * preview iframe (and a few Safari versions) because the click is no
   * longer in the trusted "user gesture" call stack. The fix is the
   * label+input HTML pattern wired up in SettingsPage — this hook now
   * just receives the picked File from the change event.
   *
   * Steps:
   *   1. Validate the file via lib/imageUpload (size + MIME).
   *   2. Upload to `company-assets/{cid}/logo/{ts}-{name}` in Cloud
   *      Storage (write-protected to admins by storage.rules).
   *   3. Auto-save the resulting public URL on the company document via
   *      PUT /companies/:cid so the recruiter does not have to remember
   *      to click "Save changes" — UX consistency with Profile avatar.
   *   4. Update logoUrl + savedLogoUrl + selectedCompany so other tabs
   *      (Job positions, Dashboard, public posting) pick up the change
   *      immediately.
   */
  const handleLogoFileSelected: React.ChangeEventHandler<HTMLInputElement> = useCallback(async (e) => {
    const file = e.target.files?.[0];
    // Reset value so picking the same file twice still triggers change
    e.target.value = '';
    if (!file || !selectedCompany) return;

    setUploadingLogo(true);
    try {
      const url = await uploadImage(file, {
        prefix: 'company-assets',
        ownerId: `${selectedCompany.id}/logo`,
      });
      const updated = await api.companies.update(selectedCompany.id, { logoUrl: url });
      setLogoUrl(url);
      setSavedLogoUrl(url);
      setSelectedCompany({ ...selectedCompany, ...updated });
      toast.success('Logo uploaded');
    } catch (err) {
      if (err instanceof ImageUploadError) {
        if (err.code === 'too_large') toast.error('Image too large. Max 2MB.');
        else if (err.code === 'invalid_type') toast.error('Unsupported image type. Use PNG, JPEG or WebP.');
        else toast.error(`Logo upload failed: ${err.message}`);
      } else {
        toast.error((err as { message?: string })?.message || 'Logo upload failed. Please try again.');
      }
    } finally {
      setUploadingLogo(false);
    }
  }, [selectedCompany, setSelectedCompany]);

  /**
   * handleRemoveLogo — auto-saves the removal so the trash button has the
   * same persistence guarantee as upload (no orphan local state). The
   * Storage object itself is intentionally NOT deleted (URL still points
   * to the underlying blob if anyone has cached the link, e.g. an open
   * public posting tab) — that cleanup belongs to a periodic janitor.
   */
  const handleRemoveLogo = useCallback(async () => {
    if (!selectedCompany) return;
    setUploadingLogo(true);
    try {
      const updated = await api.companies.update(selectedCompany.id, { logoUrl: null });
      setLogoUrl('');
      setSavedLogoUrl('');
      setSelectedCompany({ ...selectedCompany, ...updated });
      toast.success('Logo removed');
    } catch (err) {
      toast.error((err as { message?: string })?.message || 'Failed to remove logo');
    } finally {
      setUploadingLogo(false);
    }
  }, [selectedCompany, setSelectedCompany]);

  const handleSaveGeneral = useCallback(async () => {
    if (!selectedCompany) return;
    setSavingGeneral(true);
    try {
      const employeeCountNum = companyEmployeeCount ? parseInt(companyEmployeeCount, 10) : undefined;
      const salaryMin = avgSalaryMin ? parseFloat(avgSalaryMin) : undefined;
      const salaryMax = avgSalaryMax ? parseFloat(avgSalaryMax) : undefined;
      const avgSalary = (salaryMin || salaryMax)
        ? { min: salaryMin, max: salaryMax, currency: avgSalaryCurrency || 'EUR' }
        : undefined;
      const updated = await api.companies.update(selectedCompany.id, {
        name: companyName,
        publicName: companyPublicName,
        description: companyDescription,
        employeeCount: employeeCountNum && !isNaN(employeeCountNum) ? employeeCountNum : undefined,
        salary: avgSalary,
        localization: offices.filter(o => o.address),
        links: links.filter(l => l.address),
        logoUrl: logoUrl || undefined,
        // Sprint 1 (D-R3.3 / BUG-10)
        defaultOutputLanguage,
      });
      setSelectedCompany({ ...selectedCompany, ...updated });
      setSavedLogoUrl(logoUrl);
      toast.success('Settings saved');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save settings');
    } finally {
      setSavingGeneral(false);
    }
  }, [selectedCompany, companyName, companyPublicName, companyDescription, companyEmployeeCount, avgSalaryMin, avgSalaryMax, avgSalaryCurrency, offices, links, logoUrl, defaultOutputLanguage, setSelectedCompany]);

  const handleDeleteCompany = useCallback(async () => {
    if (!selectedCompany) return;
    try {
      await api.companies.delete(selectedCompany.id);
      setDeleteCompanyOpen(false);
      toast.success(t('settings.deleteCompanySuccess'));
      // Redirect to root — the auth context will pick up the empty memberships list
      window.location.href = '/';
    } catch (err: any) {
      toast.error(err?.message || t('settings.deleteCompanyError'));
    }
  }, [selectedCompany, t]);

  return {
    // Permissions
    myPermission, canManageMembers, canEditCompany,
    // General
    companyName, setCompanyName, companyPublicName, setCompanyPublicName,
    companyDescription, setCompanyDescription,
    companyEmployeeCount, setCompanyEmployeeCount,
    avgSalaryMin, setAvgSalaryMin,
    avgSalaryMax, setAvgSalaryMax,
    avgSalaryCurrency, setAvgSalaryCurrency,
    savingGeneral,
    offices, setOffices,
    links, setLinks,
    logoUrl, setLogoUrl, savedLogoUrl, setSavedLogoUrl,
    // Sprint 1 (D-R3.3 / BUG-10)
    defaultOutputLanguage, setDefaultOutputLanguage,
    teams, setTeams, teamDialog, setTeamDialog, editTeam, setEditTeam, handleSaveTeam, handleDeleteTeam,
    deleteCompanyOpen, setDeleteCompanyOpen, handleDeleteCompany,
    members, setMembers, filteredMembers,
    addOpen, setAddOpen, handleInviteMemberClick, checkingMemberCap,
    editMember, setEditMember, removeId, setRemoveId,
    addEmail, setAddEmail,
    addFirstName, setAddFirstName,
    addLastName, setAddLastName,
    addPermission, setAddPermission,
    addRole: addPermission, setAddRole: setAddPermission,  // compat alias for SettingsPage
    addType, setAddType, addTeam, setAddTeam,
    editPermission, setEditPermission,
    editRole: editPermission, setEditRole: setEditPermission,  // compat alias for SettingsPage
    editMemberTeam, setEditMemberTeam, editType, setEditType,
    editActive, setEditActive, activeTab, setActiveTab,
    teamFilterName, handleTeamClick, clearTeamFilter,
    openEdit, copyInviteLink, handleResendInvitation, handleAddMember, isSendingInvite, handleEditSave, handleRemove,
    uploadingLogo, handleLogoFileSelected, handleRemoveLogo, handleSaveGeneral,
    OFFICE_TYPES, LINK_TYPES,
  };
}
